import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import type { Node as ProseMirrorNode } from '@milkdown/kit/prose/model';
import { $node } from '@milkdown/kit/utils';

import {
  CONTENT_VARIANT_NODE_NAME,
  EMPTY_DIMENSIONS_JSON,
  TOP_LEVEL_BLOCK_GROUP,
  VARIABLE_CONTENT_DIRECTIVE_NAME,
  VARIABLE_CONTENT_NODE_NAME,
  VARIANT_DIRECTIVE_NAME,
} from './constants';
import { dimensionsFromDirectiveAttributes, directiveAttributesFromDimensions } from './dimensions';
import { flattenVariantMarkdownChildren } from './utils';

type DirectiveAttributes = Record<string, string | null | undefined> | null | undefined;

/**
 * Inner variant block: `::::variant{default}` or `::::variant{channelId="…"}` (extensible keys).
 */
export const contentVariantNode = $node(CONTENT_VARIANT_NODE_NAME, () => ({
  content: 'block+',
  defining: true,
  isolating: true,
  attrs: {
    dimensions: { default: EMPTY_DIMENSIONS_JSON },
  },
  parseDOM: [
    {
      tag: `div[data-node-type="${CONTENT_VARIANT_NODE_NAME}"]`,
      getAttrs: (dom) => {
        const element = dom as HTMLElement;

        return {
          dimensions: element.getAttribute('data-dimensions') || EMPTY_DIMENSIONS_JSON,
        };
      },
    },
  ],
  toDOM: (node: ProseMirrorNode) => [
    'div',
    {
      'data-node-type': CONTENT_VARIANT_NODE_NAME,
      'data-dimensions': node.attrs.dimensions as string,
      class: 'content-variant',
    },
    0,
  ],
  parseMarkdown: {
    match: (node) => node.type === 'containerDirective' && node.name === VARIANT_DIRECTIVE_NAME,
    runner: (state, node, type) => {
      const dimensions = dimensionsFromDirectiveAttributes(node.attributes as DirectiveAttributes);
      const children = flattenVariantMarkdownChildren(node.children);
      state.openNode(type, { dimensions }).next(children).closeNode();
    },
  },
  toMarkdown: {
    match: (node) => node.type.name === CONTENT_VARIANT_NODE_NAME,
    runner: (state, node) => {
      state.openNode('containerDirective', undefined, {
        name: VARIANT_DIRECTIVE_NAME,
        attributes: directiveAttributesFromDimensions(node.attrs.dimensions),
      });
      state.next(node.content);
      state.closeNode();
    },
  },
}));

/**
 * Outer wrapper: `:::::variants` containing one or more variants.
 * Uses {@link TOP_LEVEL_BLOCK_GROUP} so it cannot nest inside `content_variant` (`block+`).
 */
export const variableContentNode = $node(VARIABLE_CONTENT_NODE_NAME, () => ({
  group: TOP_LEVEL_BLOCK_GROUP,
  content: `${CONTENT_VARIANT_NODE_NAME}+`,
  defining: true,
  isolating: true,
  parseDOM: [
    {
      tag: `div[data-node-type="${VARIABLE_CONTENT_NODE_NAME}"]`,
    },
  ],
  toDOM: () => [
    'div',
    {
      'data-node-type': VARIABLE_CONTENT_NODE_NAME,
      class: 'variable-content',
    },
    0,
  ],
  parseMarkdown: {
    match: (node) => node.type === 'containerDirective' && node.name === VARIABLE_CONTENT_DIRECTIVE_NAME,
    runner: (state, node, type) => {
      state.openNode(type).next(node.children).closeNode();
    },
  },
  toMarkdown: {
    match: (node) => node.type.name === VARIABLE_CONTENT_NODE_NAME,
    runner: (state, node) => {
      state.openNode('containerDirective', undefined, {
        name: VARIABLE_CONTENT_DIRECTIVE_NAME,
      });
      state.next(node.content);
      state.closeNode();
    },
  },
}));

/**
 * Replaces Crepe/commonmark `doc` so top-level blocks (variable_content) remain insertable.
 * `$node` replaces by id — register after Crepe's preset is loaded.
 */
export const variableContentDocNode = $node('doc', () => ({
  content: `(block | ${TOP_LEVEL_BLOCK_GROUP})+`,
  parseMarkdown: {
    match: ({ type }) => type === 'root',
    runner: (state, node, type) => {
      state.injectRoot(node, type);
    },
  },
  toMarkdown: {
    match: (node) => node.type.name === 'doc',
    runner: (state, node) => {
      // Match commonmark docSchema — do not closeNode; serializer closes root.
      state.openNode('root');
      state.next(node.content);
    },
  },
}));

/**
 * Schema plugins only — remark-directive is already registered by noticePlugin.
 * Doc override must be included so `topLevelBlock` is valid under the root.
 */
export const variableContentPlugin: MilkdownPlugin[] = [variableContentDocNode, contentVariantNode, variableContentNode].flat();
